import React, { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import AdminLayout from "@/components/layouts/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Users, Search, Ban, CheckCircle, Shield, Eye } from "lucide-react";
import { toast } from "sonner";

const AdminUsers = () => {
  const { getAuthHeaders } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetchUsers();
  }, [filter]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter !== "all") params.append("status", filter);
      const res = await axios.get(`${API}/admin/users?${params.toString()}`, {
        headers: getAuthHeaders(),
      });
      setUsers(res.data.users || []);
    } catch (error) {
      toast.error("Failed to load users");
    }
    setLoading(false);
  };

  const toggleBlock = async (userId) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/block`, {}, {
        headers: getAuthHeaders(),
      });
      toast.success("User status updated");
      fetchUsers();
    } catch (error) {
      toast.error("Failed to update user");
    }
  };

  const verifyUser = async (userId) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/verify`, {}, {
        headers: getAuthHeaders(),
      });
      toast.success("User verified");
      fetchUsers();
    } catch (error) {
      toast.error("Failed to verify user");
    }
  };

  const updateKYC = async (userId, status) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/kyc?status=${status}`, {}, {
        headers: getAuthHeaders(),
      });
      toast.success(`KYC ${status}`);
      fetchUsers();
    } catch (error) {
      toast.error("Failed to update KYC");
    }
  };

  const filteredUsers = users.filter(
    (u) =>
      u.name?.toLowerCase().includes(search.toLowerCase()) ||
      u.mobile?.includes(search) ||
      u.email?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <AdminLayout title="Users">
      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Search by name, mobile, email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="search-users-input"
          />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-full md:w-48" data-testid="filter-users">
            <SelectValue placeholder="Filter" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Users</SelectItem>
            <SelectItem value="verified">Verified</SelectItem>
            <SelectItem value="blocked">Blocked</SelectItem>
            <SelectItem value="kyc_pending">KYC Pending</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Users Table */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto" />
            </div>
          ) : filteredUsers.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Mobile</TableHead>
                    <TableHead>Referral Code</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>KYC</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{user.name}</p>
                          <p className="text-sm text-gray-500">{user.email || "No email"}</p>
                        </div>
                      </TableCell>
                      <TableCell>{user.mobile}</TableCell>
                      <TableCell>
                        <code className="text-sm bg-gray-100 px-2 py-1 rounded">
                          {user.referral_code}
                        </code>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {user.is_verified && (
                            <Badge className="bg-green-100 text-green-700">Verified</Badge>
                          )}
                          {user.is_blocked && (
                            <Badge className="bg-red-100 text-red-700">Blocked</Badge>
                          )}
                          {!user.is_verified && !user.is_blocked && (
                            <Badge className="bg-gray-100 text-gray-700">Unverified</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          className={
                            user.kyc_status === "approved"
                              ? "bg-green-100 text-green-700"
                              : user.kyc_status === "rejected"
                              ? "bg-red-100 text-red-700"
                              : "bg-yellow-100 text-yellow-700"
                          }
                        >
                          {user.kyc_status || "Pending"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          {!user.is_verified && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => verifyUser(user.id)}
                              className="text-green-600"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => toggleBlock(user.id)}
                            className={user.is_blocked ? "text-green-600" : "text-red-600"}
                          >
                            <Ban className="w-4 h-4" />
                          </Button>
                          {user.kyc_status === "pending" && user.kyc_data && (
                            <>
                              <Button
                                size="sm"
                                onClick={() => updateKYC(user.id, "approved")}
                                className="bg-green-500 hover:bg-green-600 text-white"
                              >
                                Approve KYC
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => updateKYC(user.id, "rejected")}
                                className="text-red-600"
                              >
                                Reject
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="p-12 text-center">
              <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No users found</p>
            </div>
          )}
        </CardContent>
      </Card>
    </AdminLayout>
  );
};

export default AdminUsers;
